import { Button } from '@react-spectrum/s2';
import { useCallback } from 'react';

import { useBridgeApi } from '@/api/BridgeApi';
import {
	TYPE_TITLES,
	isArtifactActionable,
} from '@/components/artifact/panelMeta';
import { useEnsureSetupSnapshot } from '@/components/artifact/useEnsureSetupSnapshot';
import { useGoToNextStep } from '@/components/artifact/useGoToNextStep';
import { useStepSubmitted } from '@/components/artifact/useStepSubmitted';
import { EmailBodyEditor } from '@/components/EmailBodyEditor/EmailBodyEditor.tsx';
import { useOpenChatPanel } from '@/hooks/useOpenChatPanel';
import { useOptimisticState } from '@/hooks/useOptimisticState';
import { ChatPanelLayout } from '@/spaces/chat/panels/ChatPanelLayout';
import { trackEvent } from '@/utils/fullstory';

import type { EmailPlaintext } from '../../../../../bridge/types/artifact.ts';
import { isSingleSendableEmailProcess } from '../../../../../bridge/utils/setupCardItems.ts';

interface PlaintextEmailArtifactPanelProps {
	artifact: EmailPlaintext;
	onBack?: () => void;
	onClose: () => void;
	style?: React.CSSProperties;
}

export const PlaintextEmailArtifactPanel = ({
	artifact,
	onBack,
	onClose,
	style,
}: PlaintextEmailArtifactPanelProps) => {
	const bridgeApi = useBridgeApi();
	const goToNextItem = useGoToNextStep();
	const openChatPanel = useOpenChatPanel();
	useEnsureSetupSnapshot(artifact.process_id);

	// A one-off email send (a standalone edit, or a campaign whose only sendable
	// piece is this email) gets the "Send now" path through recipients -> schedule,
	// identical to email_html (EmailArtifactPanel). A genuine multi-piece campaign
	// keeps "Approve": its pieces go out together at campaign activation.
	const currentChatId = bridgeApi.user_state?.current_chat_id ?? null;
	const currentChat = currentChatId
		? (bridgeApi.user_state?.chats?.[currentChatId] ?? null)
		: null;
	const isStandaloneEdit =
		currentChat?.linked_item?.type === 'artifact' &&
		currentChat.linked_item.id === artifact.id;
	const snapshot = artifact.process_id
		? (bridgeApi.user_state?.process_setup_snapshots?.[artifact.process_id] ??
			null)
		: null;
	const isSingleEmailSend =
		isStandaloneEdit ||
		(snapshot != null && isSingleSendableEmailProcess(snapshot));

	const { submittedView, markSubmitted } = useStepSubmitted({
		title: 'Email confirmed',
		summary: 'Ready for the next step in your campaign setup.',
		onClose,
		onGoToNextStep: () => {
			if (artifact.process_id) goToNextItem(artifact.process_id, artifact.id);
		},
	});

	const [localSubject, setLocalSubject] = useOptimisticState(
		artifact.content.subject,
	);
	const [localBody, setLocalBody] = useOptimisticState(artifact.content.body);

	const handleSubjectCommit = useCallback(
		(value: string) => {
			const trimmed = value.trim();
			if (trimmed && trimmed !== artifact.content.subject) {
				bridgeApi.sendAction({
					type: 'do_edit_artifact',
					artifact_id: artifact.id,
					edits: { subject: trimmed },
				});
			}
			setLocalSubject(null);
		},
		[artifact.content.subject, artifact.id, bridgeApi, setLocalSubject],
	);

	const handleBodyCommit = useCallback(
		(value: string) => {
			if (value.trim() && value !== artifact.content.body) {
				bridgeApi.sendAction({
					type: 'do_edit_artifact',
					artifact_id: artifact.id,
					edits: { body: value },
				});
			}
			setLocalBody(null);
		},
		[artifact.content.body, artifact.id, bridgeApi, setLocalBody],
	);

	const handleSubmit = useCallback(() => {
		if (!artifact.process_id) return;
		trackEvent('Email Submitted', { artifact_id: artifact.id });
		bridgeApi.sendAction({
			type: 'do_mark_setup_item_completed',
			process_id: artifact.process_id,
			item_id: artifact.id,
		});
		if (isSingleEmailSend) {
			openChatPanel({
				panel_type: 'process_recipients',
				item_id: artifact.process_id,
			});
			return;
		}
		markSubmitted();
	}, [
		bridgeApi,
		openChatPanel,
		artifact.id,
		artifact.process_id,
		isSingleEmailSend,
		markSubmitted,
	]);

	const title = TYPE_TITLES.email_plaintext ?? 'Review Email';
	const showSubmitButton = isArtifactActionable(artifact) && !submittedView;
	const actionRenderer = showSubmitButton
		? () => (
				<Button
					type="button"
					variant="accent"
					onPress={handleSubmit}
					isDisabled={!artifact.process_id}
				>
					{isSingleEmailSend ? 'Send now' : 'Approve'}
				</Button>
			)
		: undefined;

	return (
		<ChatPanelLayout
			title={title}
			actionRenderer={actionRenderer}
			isLoading={false}
			onBack={onBack}
			onClose={onClose}
			style={style}
		>
			{submittedView ?? (
				<div
					className="glass-card-sm flex min-h-0 flex-1 flex-col overflow-hidden"
				>
					<input
						type="text"
						aria-label="Subject"
						className="shrink-0 border-b border-(--glass-border) bg-transparent
							px-3 py-2.5 text-base font-bold text-(--text-primary)
							placeholder:font-normal placeholder:text-(--text-disabled)
							focus:outline-none"
						value={localSubject}
						onChange={(event) => setLocalSubject(event.currentTarget.value)}
						onBlur={() => handleSubjectCommit(localSubject)}
						placeholder="Email subject"
					/>
					<EmailBodyEditor
						markdown={localBody}
						onCommit={handleBodyCommit}
						placeholder="Write your email…"
					/>
				</div>
			)}
		</ChatPanelLayout>
	);
};
